import XRegExp from 'xregexp';
import Expression from './expression';
import {
  ExpressionArrayType,
  ExpressionNodeType,
  ExpressionTreeType,
  ExpressionType,
  LogicalOperatorType,
  NodeOperators,
  NodeOperatorType,
  operatorsWithNotNullValue,
} from './types';

const nodeOpRegexp = new RegExp(NodeOperators.join('|'));
const dataKeyRegexp = /\{(.*?}*)\}/g;
const logicalOpRegexp = /\|\||\&\&/g;

class ExpressionBuilder {
  //#region public static

  public static fromString = (expressionString: string) => {
    return new Expression(this.tryParse(expressionString));
  };

  public static fromArray = (expressionArray: ExpressionArrayType) => {
    this.validateExpressionArray(expressionArray);

    return new Expression(
      ExpressionBuilder.expressionArrayToTree(expressionArray),
    );
  };

  public static fromNode = (expressionNode: ExpressionNodeType) => {
    return new Expression(expressionNode);
  };

  //#endregion

  //#region private

  private static tryParseToNode = (expression: string): ExpressionNodeType => {
    const operator = ExpressionBuilder.getComparisonOperator(expression);
    const [leftPart, rightPart] = expression.split(nodeOpRegexp);
    const dataKey = ExpressionBuilder.getDataKey(leftPart);
    const value = ExpressionBuilder.getValue(rightPart);

    const node: ExpressionNodeType = {
      dataKey,
      value,
      operator,
    };

    ExpressionBuilder.validateNode(node);
    return node;
  };

  private static tryParse = (
    expression: string,
  ): ExpressionTreeType | ExpressionNodeType => {
    // NOTE: Use XRegExp because match don't support recursion
    const arr = XRegExp.matchRecursive(expression, '\\(', '\\)', 'g');
    if (arr.length < 2) {
      const node = this.tryParseToNode(expression);
      return node;
    }

    const [left, right] = arr;
    const rest = expression.replace(left, '').replace(right, '');

    return {
      operandOne: this.tryParse(left),
      operandTwo: this.tryParse(right),
      operator: ExpressionBuilder.getLogicalOperator(rest),
    };
  };

  private static expressionArrayToTree = ({
    nodes,
    operators,
  }: ExpressionArrayType): ExpressionType => {
    const {
      nodes: [result],
    } = ExpressionBuilder.buildExpressionTree(nodes, operators);

    return result;
  };

  private static buildExpressionTree = (
    nodes: ExpressionType[],
    operators: LogicalOperatorType[],
  ): { nodes: ExpressionType[]; operators: LogicalOperatorType[] } => {
    if (!operators.length) {
      return { nodes, operators };
    }

    const o = [...operators];
    const firstAndOperatorIndex = o.indexOf('&&');
    const indexToMerge = Math.max(firstAndOperatorIndex, 0);

    o.splice(indexToMerge, 1);
    const exp: ExpressionTreeType = {
      operandOne: nodes[indexToMerge],
      operandTwo: nodes[indexToMerge + 1],
      operator: firstAndOperatorIndex === -1 ? '||' : '&&',
    };

    const n = [
      ...nodes.reduce<ExpressionType[]>((arr, cur, i) => {
        if (i === indexToMerge) return [...arr, exp];
        if (i === indexToMerge + 1) return [...arr];
        return [...arr, cur];
      }, []),
    ];

    return ExpressionBuilder.buildExpressionTree(n, o);
  };

  private static getDataKey = (str: string): string => {
    const match = str.match(dataKeyRegexp);
    if (!match) throw new Error('The condition does not include dataKey');
    return match[0].slice(1, -1);
  };

  private static getValue = (val: string | undefined | null): string | null => {
    if (!val) return null;
    return JSON.parse(val);
  };

  private static getComparisonOperator = (str: string): NodeOperatorType => {
    const operator = str.match(nodeOpRegexp)?.[0];
    if (!operator)
      throw new Error('The condition does not include any comparison operator');

    return operator as NodeOperatorType;
  };

  private static getLogicalOperator = (str: string): LogicalOperatorType => {
    const operator = str.match(logicalOpRegexp)?.[0];
    if (!operator)
      throw new Error('The condition does not include any logical operator');

    return operator as LogicalOperatorType;
  };

  private static validateExpressionArray = ({
    nodes,
    operators,
  }: ExpressionArrayType) => {
    if (nodes.length - 1 !== operators.length)
      throw new Error(
        "The expression array doesn't contain the required number of logical operators",
      );

    nodes.forEach(ExpressionBuilder.validateNode);
  };

  private static validateNode(node: ExpressionNodeType) {
    if (
      operatorsWithNotNullValue.includes(node.operator) &&
      (node.value === null || node.value === null)
    ) {
      throw new Error(
        `The value is required for the operator ${node.operator}`,
      );
    }
  }

  //#endregion
}

export default ExpressionBuilder;
